import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CoinsIcon, CreditCardIcon, HistoryIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getUserCredits, getCreditTransactions, requestCashout } from '@/lib/api/credits';
import type { UserCredits, CreditTransaction } from '@/types/database.types';
import Button from '@/components/form/Button';
import Pagination from '@/components/Pagination';
import { formatDistanceToNow } from 'date-fns';

export default function Credits() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cashoutAmount, setCashoutAmount] = useState<number>(5);
  const [processingCashout, setProcessingCashout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchCredits();
    fetchTransactions(currentPage);
  }, [currentPage]);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserCredits();
      
      // Validate the data to ensure it contains valid numbers
      if (isNaN(data.balance) || isNaN(data.lifetime_earned) || isNaN(data.lifetime_withdrawn)) {
        console.error('Invalid credit values received:', data);
        setError('Invalid credit data received');
        // Set default values to prevent NaN display
        setCredits({
          balance: 0,
          lifetime_earned: 0,
          lifetime_withdrawn: 0,
          eligible_for_cashout: false,
          min_cashout_amount: 5.00
        });
      } else {
        setCredits(data);
        
        // Set default cashout amount to minimum or balance (whichever is lower)
        if (data.eligible_for_cashout) {
          setCashoutAmount(Math.min(data.balance, data.min_cashout_amount));
        }
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
      setError('Failed to load credit information');
      toast.error('Failed to load credit information');
      
      // Set default values on error
      setCredits({
        balance: 0,
        lifetime_earned: 0,
        lifetime_withdrawn: 0,
        eligible_for_cashout: false,
        min_cashout_amount: 5.00
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page: number) => {
    try {
      setTransactionsLoading(true);
      const response = await getCreditTransactions(page, ITEMS_PER_PAGE);
      
      // Validate transaction amounts
      const validTransactions = response.data.map(transaction => ({
        ...transaction,
        amount: isNaN(transaction.amount) ? 0 : transaction.amount
      }));
      
      setTransactions(validTransactions);
      setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE));
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transaction history');
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleCashout = async () => {
    if (!credits || !credits.eligible_for_cashout) return;
    
    try {
      setProcessingCashout(true);
      await requestCashout(cashoutAmount);
      toast.success('Cashout request submitted successfully');
      
      // Refresh data
      await fetchCredits();
      await fetchTransactions(1);
      setCurrentPage(1);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to process cashout request');
      }
    } finally {
      setProcessingCashout(false);
    }
  };

  const formatCurrency = (amount: number) => {
    // Ensure amount is a valid number
    const validAmount = isNaN(amount) ? 0 : amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(validAmount);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <AlertCircleIcon className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeftIcon className="h-4 w-4" />}
        onClick={() => navigate('/dashboard')}
        className="mb-6"
      >
        Back to Dashboard
      </Button>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <div className="px-6 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600">
          <h1 className="text-2xl font-bold text-white flex items-center">
            <CoinsIcon className="h-6 w-6 mr-2" />
            Your Cash Balance
          </h1>
        </div>

        {loading ? (
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
          </div>
        ) : credits ? (
          <div className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                <p className="flex items-center">
                  <AlertCircleIcon className="h-5 w-5 mr-2" />
                  {error}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="text-sm text-yellow-700 mb-1">Current Balance</div>
                <div className="text-2xl font-bold text-yellow-800">{formatCurrency(credits.balance)}</div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="text-sm text-green-700 mb-1">Lifetime Earned</div>
                <div className="text-2xl font-bold text-green-800">{formatCurrency(credits.lifetime_earned)}</div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="text-sm text-blue-700 mb-1">Lifetime Withdrawn</div>
                <div className="text-2xl font-bold text-blue-800">{formatCurrency(credits.lifetime_withdrawn)}</div>
              </div>
            </div>

            <div className="mt-8 bg-white p-6 rounded-lg border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCardIcon className="h-5 w-5 mr-2 text-yellow-600" />
                Cash Out
              </h2>
              
              <div className="flex items-center text-gray-700 mb-4">
                <div className={`mr-2 h-3 w-3 rounded-full ${credits.eligible_for_cashout ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span>
                  {credits.eligible_for_cashout 
                    ? `Eligible to cash out (minimum: ${formatCurrency(credits.min_cashout_amount)})` 
                    : `Cash out possible from ${formatCurrency(credits.min_cashout_amount)}`}
                </span>
              </div>
              
              {credits.eligible_for_cashout ? (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="cashout-amount" className="block text-sm font-medium text-gray-700 mb-1">
                      Amount to cash out
                    </label>
                    <div className="flex items-center">
                      <input
                        id="cashout-amount"
                        type="number"
                        min={credits.min_cashout_amount}
                        max={credits.balance}
                        step="0.01"
                        value={cashoutAmount}
                        onChange={(e) => setCashoutAmount(parseFloat(e.target.value))}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                      />
                      <span className="ml-2 text-gray-500">USD</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-4">
                    <Button
                      onClick={handleCashout}
                      loading={processingCashout}
                      disabled={processingCashout || cashoutAmount < credits.min_cashout_amount || cashoutAmount > credits.balance}
                      className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
                    >
                      Cash Out {formatCurrency(cashoutAmount)}
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => navigate('/dashboard')}
                    >
                      Keep Earning
                    </Button>
                  </div>
                  
                  <p className="text-sm text-gray-500">
                    <AlertCircleIcon className="inline-block h-4 w-4 mr-1" />
                    Cashout requests are processed within 3-5 business days.
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-700">
                    You need at least {formatCurrency(credits.min_cashout_amount)} to request a cashout.
                    Keep participating in CashSnaps to earn more!
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="mt-4"
                  >
                    Find CashSnaps
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            Failed to load credit information. Please try again later.
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
          <h2 className="text-xl font-bold text-white flex items-center">
            <HistoryIcon className="h-5 w-5 mr-2" />
            Transaction History
          </h2>
        </div>

        {transactionsLoading ? (
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDistanceToNow(new Date(transaction.created_at), { addSuffix: true })}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {transaction.type === 'earning' && transaction.snap_title ? (
                        <span>Earnings from "{transaction.snap_title}"</span>
                      ) : (
                        transaction.description || (transaction.type === 'withdrawal' ? 'Cashout request' : 'Unknown transaction')
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        transaction.type === 'earning' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {transaction.type === 'earning' ? 'Earning' : 'Withdrawal'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        {getStatusIcon(transaction.status)}
                        <span className="ml-1 capitalize">{transaction.status}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                      <span className={transaction.type === 'earning' ? 'text-green-600' : 'text-blue-600'}>
                        {transaction.type === 'earning' ? '+' : '-'}{formatCurrency(transaction.amount)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <div className="px-6 py-4 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        ) : (
          <div className="p-6 text-center">
            <HistoryIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions yet</h3>
            <p className="text-gray-500 mb-6">
              Participate in CashSnaps to start earning credits!
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
            >
              Find CashSnaps
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}